// DEPENDENCIES
import { FormikValues } from "formik";
import * as Sentry from "@sentry/react";

// TYPES
import { AppDispatch } from "../../app/store";
import { InputType, Step } from "../../types";

// UTILS
import { onUpdateInfoStep } from "../../reducers/infoStepReducer";
import { onUpdateStep } from "../../reducers/onboardingDataReducer";
import {
  asCuit,
  getPartnerCompanyType,
  isAllariaMasOnboarding,
  isAllariaOnboarding,
  isJuridicalCuit,
  isNextStepCompleted,
  isUnifiedOnboarding,
  yesOrNoBoolOptions,
} from "../../utils";
import { getCompanyData } from "../../utils/companies";
import { authEmail } from "../../connectors/connectors";
import {
  updateDraftBase,
  updateSignatureRequestId,
} from "../../reducers/authReducer";
import { getDraftBase } from "../../utils/draftBase";
import { logAnalyticsAndHotjarEvent } from "../../main";
import {
  cuitPFRequired,
  emailRequired,
  radioOrSingleSelectRequired,
  smallStringRequired,
} from "../../validations";
import { isDev } from "../../app/utils";

// COMPONENTS
import UnificationCard from "../../components/UnificationCard/UnificationCard";

const basicInformationStep = (name?: string): Omit<Step, "step"> => ({
  label: "basicInformation",
  name: name || "Cuenta personal",
  titleOfStep: "Información básica",
  disableBackNavigation: isNextStepCompleted,
  fields: [
    {
      initialValue: "",
      type: "cuit",
      name: "cuit",
      label: "CUIT / CUIL",
      placeholder: "XX-XXXXXXXX-X",
      validation: cuitPFRequired,
      formatInPreConfirmation: (value) => asCuit(value),
      ...(isDev ? { disabledByUrlParams: ["cuit", "accountCuit"] } : {}),
    },
    {
      initialValue: "",
      type: "email",
      name: "email",
      label: "Mail",
      validation: emailRequired,
      placeholder: "direccion@mail.com",
      disabledByUrlParams: isDev ? ["email", "accountEmail"] : "email",
    },
    {
      name: "referredBool",
      type: "radio",
      label: "¿Fuiste referido?",
      validation: radioOrSingleSelectRequired(yesOrNoBoolOptions),
      initialValue: "",
      options: yesOrNoBoolOptions,
      disabledByUrlParams: "ref",
    },
    {
      conditional: {
        name: "referredBool",
        value: "SI",
      },
      initialValue: "",
      type: "text",
      name: "referrer",
      label: "Nombre o mail de quien te refirió",
      validation: smallStringRequired,
      disabledByUrlParams: "ref",
    },
    {
      name: "labelReferrer",
      type: "label" as InputType,
      label: "",
      initialValue: "",
      hideField: !isAllariaOnboarding(),
    },
    {
      name: "unifiedLabel",
      type: "label" as InputType,
      label: "",
      initialValue: "",
      text: <UnificationCard />,
      hideField: !(isUnifiedOnboarding() && isAllariaMasOnboarding()),
    },
  ],
  onContinue: async (values: FormikValues, dispatch) => {
    if (!dispatch || !values) throw new Error("Error: dispatch not found");

    //To prevent failed loop
    dispatch(onUpdateInfoStep(null));

    const { cuit } = values;

    return isJuridicalCuit(cuit)
      ? handleInvalidCuit(dispatch)
      : await createOnboarding(dispatch, values);
  },
});

const handleInvalidCuit = (dispatch: AppDispatch) => {
  dispatch(onUpdateInfoStep(null));
  dispatch(onUpdateStep("information-step"));
  throw new Error("CUIT no válido");
};

export const createOnboarding = async (
  dispatch: AppDispatch,
  values: FormikValues,
  oldAuth?: boolean,
) => {
  const { email } = values;
  const partnerCompanyType = getPartnerCompanyType(values);
  const draftBase = getDraftBase(values);

  const res = await authEmail({
    email,
    es: true,
    companyType: getCompanyData().companyName,
    ...(partnerCompanyType ? { partnerCompanyType } : {}),
    oldAuth: !!oldAuth,
    draftBase,
  });

  dispatch(updateSignatureRequestId(res.data.signatureRequestId));
  dispatch(updateDraftBase(draftBase));
  logAnalyticsAndHotjarEvent("step_information_basic_completed_pf");

  if (email) Sentry.setUser({ email });
};

export default basicInformationStep;
